/*
 * Members and pending invites (`sitebrew-api#252`, M3 of design note 0001) —
 * the console half of `sitebrew-api#259`/`#260`'s API.
 *
 * No fleet screen exists to copy — the reference CMS design has no multi-member
 * workspace — so this is written fresh, in the same shape `APITokensSettings`
 * already has for the same reasons: a plain bordered table (a workspace holds
 * a small number of members and invites, nothing to page through — [MEM-131]),
 * refusals drawn inside the dialog that caused them in the API's own words
 * (no toasts, `LayoutNotice` records the same decision for this console), and
 * a message cleared on a dialog's *open* rather than its close
 * (`APITokensSettings`'s own long note on `openCreateDialog` explains why the
 * closing edge is deliberately empty — the same reasoning applies here).
 *
 * One thing this screen has that `APITokensSettings` does not: **role
 * gating**. The API enforces the real boundary (owner-only for invite/
 * remove/revoke, `forbidden` otherwise) — this screen's `isOwner` prop only
 * decides what is drawn, the same "not the boundary" note
 * `SettingsLayout`/`apiTokensRoute` already carry for the workspace address.
 * A `member` sees who is here and nothing to act on; the pending-invites
 * section is not shown at all for a `member`; the API's own `listInvites`
 * answers an empty list rather than `forbidden` there (`sitebrew-api`'s own
 * doc comment on it), and an empty table with no way to explain why is worse
 * than no section.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTranslation, type StringKey } from "@/hooks/useTranslation";
import { ApiError } from "@/lib/api";
import {
  useCreateInvite,
  useInvites,
  useMembers,
  useRemoveMember,
  useRevokeInvite,
  type Invite,
  type Member,
} from "@/lib/members";
import { formatDistanceToNow } from "date-fns";
import { Mail, Trash2, UserPlus } from "lucide-react";
import { useState } from "react";

/** The same narrowing `APITokensSettings.tsx`'s own `said` gives, word for word. */
function said(t: (key: StringKey) => string, failure: unknown, fallback: StringKey) {
  return failure instanceof ApiError ? failure.message : t(fallback);
}

const ago = (at: number) => formatDistanceToNow(new Date(at), { addSuffix: true });

export function MembersSettings({ workspaceId, isOwner }: { workspaceId: string; isOwner: boolean }) {
  const { t } = useTranslation();

  const members = useMembers(workspaceId);
  const invites = useInvites(workspaceId, isOwner);
  const removeMember = useRemoveMember(workspaceId);
  const createInvite = useCreateInvite(workspaceId);
  const revokeInvite = useRevokeInvite(workspaceId);

  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"owner" | "member">("member");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<{ userId: string; label: string } | null>(null);
  const [inviteToRevoke, setInviteToRevoke] = useState<{ id: string; email: string } | null>(null);

  // Cleared on open only — the same reason `APITokensSettings.tsx`'s
  // `openCreateDialog` gives at length for its own create dialog.
  const openInviteDialog = (open: boolean) => {
    if (open) {
      createInvite.reset();
      setEmailError(null);
    }
    setIsInviteDialogOpen(open);
  };

  const openRemoveDialog = (member: { userId: string; label: string } | null) => {
    if (member) removeMember.reset();
    setMemberToRemove(member);
  };

  const openRevokeDialog = (invite: { id: string; email: string } | null) => {
    if (invite) revokeInvite.reset();
    setInviteToRevoke(invite);
  };

  const handleInvite = () => {
    const email = inviteEmail.trim();
    if (!email) {
      setEmailError(t("invites.messages.emailRequired"));
      return;
    }
    setEmailError(null);
    createInvite.mutate(
      { email, role: inviteRole },
      {
        onSuccess: () => {
          setInviteEmail("");
          setInviteRole("member");
          setIsInviteDialogOpen(false);
        },
      },
    );
  };

  const handleRemove = () => {
    if (!memberToRemove) return;
    removeMember.mutate(memberToRemove.userId, { onSuccess: () => setMemberToRemove(null) });
  };

  const handleRevoke = () => {
    if (!inviteToRevoke) return;
    revokeInvite.mutate(inviteToRevoke.id, { onSuccess: () => setInviteToRevoke(null) });
  };

  const memberRows = members.data ?? [];
  const inviteRows = invites.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-medium">{t("members.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("members.description")}</p>
        </div>
        {isOwner ? (
          <Button onClick={() => openInviteDialog(true)} className="flex items-center gap-2 shrink-0">
            <UserPlus className="h-4 w-4" />
            {t("invites.inviteButton")}
          </Button>
        ) : null}
      </div>

      {/* A refetch that failed while the previous answer is still in hand — the
          same reasoning `APITokensSettings.tsx` gives for its own listing. */}
      {members.error && members.data ? (
        <p role="alert" className="text-sm text-destructive">
          {said(t, members.error, "members.messages.loadError")}
        </p>
      ) : null}

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("members.table.name")}</TableHead>
              <TableHead>{t("members.table.email")}</TableHead>
              <TableHead>{t("members.table.role")}</TableHead>
              <TableHead>{t("members.table.joined")}</TableHead>
              {isOwner ? <TableHead className="text-right">{t("members.table.actions")}</TableHead> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.isLoading || (members.error && !members.data) || memberRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isOwner ? 5 : 4} className="text-center text-muted-foreground">
                  {members.isLoading
                    ? "Loading..."
                    : members.error
                      ? said(t, members.error, "members.messages.loadError")
                      : "No members are available in this workspace."}
                </TableCell>
              </TableRow>
            ) : (
              memberRows.map((member) => (
                <MemberRow
                  key={member.userId}
                  member={member}
                  isOwner={isOwner}
                  onRemove={() => openRemoveDialog({ userId: member.userId, label: member.name || member.email })}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/*
        Not shown for a `member`: the API's own `listInvites` answers an
        empty list rather than `forbidden` for one, and an empty table with
        no way to explain why it is empty is worse than no section at all
        (this file's own top note).
      */}
      {isOwner ? (
        <>
          <Separator />
          <div>
            <h3 className="text-lg font-medium">{t("invites.title")}</h3>
            <p className="text-sm text-muted-foreground">{t("invites.description")}</p>
          </div>

          {invites.error && invites.data ? (
            <p role="alert" className="text-sm text-destructive">
              {said(t, invites.error, "invites.messages.loadError")}
            </p>
          ) : null}

          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("invites.table.email")}</TableHead>
                  <TableHead>{t("invites.table.role")}</TableHead>
                  <TableHead>{t("invites.table.invited")}</TableHead>
                  <TableHead>{t("invites.table.expires")}</TableHead>
                  <TableHead className="text-right">{t("invites.table.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invites.isLoading || (invites.error && !invites.data) || inviteRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      {invites.isLoading
                        ? "Loading..."
                        : invites.error
                          ? said(t, invites.error, "invites.messages.loadError")
                          : t("invites.noInvites")}
                    </TableCell>
                  </TableRow>
                ) : (
                  inviteRows.map((invite) => (
                    <InviteRow
                      key={invite.id}
                      invite={invite}
                      onRevoke={() => openRevokeDialog({ id: invite.id, email: invite.email })}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </>
      ) : null}

      {/* Invite dialog */}
      <Dialog open={isInviteDialogOpen} onOpenChange={openInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("invites.create.title")}</DialogTitle>
            <DialogDescription>{t("invites.create.description")}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="invite-email" className="text-right">
                {t("invites.create.emailLabel")}
              </Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="col-span-3"
                placeholder={t("invites.create.emailPlaceholder")}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="invite-role" className="text-right">
                {t("invites.create.roleLabel")}
              </Label>
              <Select value={inviteRole} onValueChange={(value) => setInviteRole(value as "owner" | "member")}>
                <SelectTrigger id="invite-role" className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">{t("members.role.member")}</SelectItem>
                  <SelectItem value="owner">{t("members.role.owner")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {emailError || createInvite.error ? (
              <p role="alert" className="text-sm text-destructive">
                {emailError ?? said(t, createInvite.error, "invites.messages.createError")}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="submit" onClick={handleInvite} disabled={createInvite.isPending}>
              <Mail className="h-4 w-4" />
              {createInvite.isPending ? t("invites.create.sending") : t("invites.create.inviteButton")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove member dialog */}
      <AlertDialog open={!!memberToRemove} onOpenChange={(open: boolean) => !open && openRemoveDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("members.remove.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("members.remove.description", { name: memberToRemove?.label ?? "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {removeMember.error ? (
            <p role="alert" className="text-sm text-destructive">
              {said(t, removeMember.error, "members.messages.removeError")}
            </p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>{t("members.remove.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                // Ours on success only — the same reason `APITokensSettings.tsx`'s
                // delete confirm gives: Radix closing the dialog for us would
                // discard a refusal nobody had a chance to read.
                event.preventDefault();
                handleRemove();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("members.remove.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke invite dialog */}
      <AlertDialog open={!!inviteToRevoke} onOpenChange={(open: boolean) => !open && openRevokeDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("invites.revoke.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("invites.revoke.description", { email: inviteToRevoke?.email ?? "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {revokeInvite.error ? (
            <p role="alert" className="text-sm text-destructive">
              {said(t, revokeInvite.error, "invites.messages.revokeError")}
            </p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>{t("invites.revoke.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                handleRevoke();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("invites.revoke.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MemberRow({
  member,
  isOwner,
  onRemove,
}: {
  member: Member;
  isOwner: boolean;
  onRemove: () => void;
}) {
  const { t } = useTranslation();

  return (
    <TableRow>
      <TableCell className="font-medium">{member.name || member.email}</TableCell>
      <TableCell>{member.email}</TableCell>
      <TableCell>
        <Badge variant={member.role === "owner" ? "default" : "secondary"}>
          {member.role === "owner" ? t("members.role.owner") : t("members.role.member")}
        </Badge>
      </TableCell>
      <TableCell>{ago(member.createdAt)}</TableCell>
      {isOwner ? (
        <TableCell className="text-right">
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-destructive hover:text-destructive"
            aria-label={`${t("members.remove.confirm")} ${member.name || member.email}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </TableCell>
      ) : null}
    </TableRow>
  );
}

function InviteRow({ invite, onRevoke }: { invite: Invite; onRevoke: () => void }) {
  const { t } = useTranslation();

  return (
    <TableRow>
      <TableCell className="font-medium">{invite.email}</TableCell>
      <TableCell>
        <Badge variant={invite.role === "owner" ? "default" : "secondary"}>
          {invite.role === "owner" ? t("members.role.owner") : t("members.role.member")}
        </Badge>
      </TableCell>
      <TableCell>{ago(invite.createdAt)}</TableCell>
      <TableCell>{ago(invite.expiresAt)}</TableCell>
      <TableCell className="text-right">
        <Button
          variant="ghost"
          size="sm"
          onClick={onRevoke}
          className="text-destructive hover:text-destructive"
          aria-label={`${t("invites.revoke.confirm")} ${invite.email}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}
